// React Compiler opt-out: due-date copy/urgency derive from moment() (current time) in
// render — impure reads the compiler would memoize as dependency-free, freezing the copy
// for the session. Same class as the TeacherDashboard AutoArchive opt-outs.
'use no memo';

import {Link} from 'react-router';
import moment from 'moment-timezone';
import React, {useState} from 'react';

import {Icon, Text, WithTooltip} from '@albert-io/atomic';
import {pluralize} from 'lib/stringUtils';
import StatusBadge from './StatusBadge';
import type {StudentAssignment} from './StudentAssignment.types';
import {StudentAssignmentStatus} from 'gql/types';
import type {TextColor} from '@albert-io/atomic/atoms/Text/Text.react';

interface Props {
  assignment: StudentAssignment;
  hasTeacherFeedback?: boolean;
  /** Fired when the row is opened, for callers that track whether it has been read. */
  onClick?: () => void;
}

export default function Assignment({assignment, hasTeacherFeedback = false, onClick}: Props) {
  const [showExactDueDate, setShowExactDueDate] = useState(false);

  const isSubmitted = assignment.status === StudentAssignmentStatus.Completed;
  const isClosed = assignment.status === StudentAssignmentStatus.Closed;
  const isPastDue = assignment.status === StudentAssignmentStatus.PastDue;

  const dueDate = moment(assignment.dueDate);
  const now = moment();
  const diffMinutes = dueDate.diff(now, 'minutes');

  let dueCopyColor: TextColor = 'tertiary';

  if (isSubmitted || isClosed) {
    dueCopyColor = 'tertiary';
  } else if (diffMinutes < 0) {
    dueCopyColor = 'negative';
  } else if (diffMinutes >= 0 && diffMinutes <= 59) {
    dueCopyColor = 'warning';
  }

  let dueCopyText: string;

  const absDiffMinutes = Math.abs(diffMinutes);
  const absDiffHours = Math.abs(dueDate.diff(now, 'hours'));
  const absDiffDays = Math.abs(dueDate.diff(now, 'days'));
  const absDiffWeeks = Math.abs(dueDate.diff(now, 'weeks'));
  const absDiffMonths = Math.abs(dueDate.diff(now, 'months'));

  if (isSubmitted || isClosed) {
    dueCopyText = getFullDueDate();
  } else if (isPastDue) {
    if (absDiffMinutes <= 59) {
      dueCopyText = `Due ${absDiffMinutes} ${pluralize('minute', absDiffMinutes)} ago`;
    } else if (absDiffHours < 24) {
      dueCopyText = `Due ${absDiffHours} ${pluralize('hour', absDiffHours)} ago`;
    } else if (absDiffHours < 48) {
      dueCopyText = 'Due yesterday';
    } else if (absDiffDays < 7) {
      dueCopyText = `Due ${absDiffDays} ${pluralize('day', absDiffDays)} ago`;
    } else if (absDiffMonths < 1) {
      dueCopyText = `Due ${absDiffWeeks} ${pluralize('week', absDiffWeeks)} ago`;
    } else {
      dueCopyText = `Due ${absDiffMonths} ${pluralize('month', absDiffMonths)} ago`;
    }
  } else {
    if (absDiffMinutes < 60) {
      dueCopyText = `Due in ${absDiffMinutes} ${pluralize('minute', absDiffMinutes)}`;
    } else if (absDiffHours < 4) {
      dueCopyText = `Due in ${absDiffHours} ${pluralize('hour', absDiffHours)}`;
    } else if (dueDate.isBefore(moment().endOf('day'))) {
      dueCopyText = 'Due today';
    } else if (dueDate.isBefore(moment().add(1, 'day').endOf('day'))) {
      dueCopyText = 'Due tomorrow';
    } else if (dueDate.isBefore(moment().endOf('week'))) {
      dueCopyText = `Due ${dueDate.format('dddd')}`;
    } else if (
      dueDate.isAfter(moment().endOf('week')) &&
      dueDate.isBefore(moment().add(1, 'week').endOf('week'))
    ) {
      dueCopyText = 'Due next week';
    } else {
      dueCopyText = getFullDueDate();
    }
  }

  function getFullDueDate() {
    return `Due ${dueDate.format('MMMM Do, YYYY h:mma')} ${moment.tz.zone(moment.tz.guess())?.abbr(dueDate.valueOf()) ?? ''}`;
  }

  return (
    <Link
      className='gap-x2 rounded-x1 p-x0-half hover:background-primary-hover active:background-primary-pressed flex items-center no-underline'
      to={`/assignment/${assignment.assignmentId}`}
      onClick={onClick}
      onBlur={() => setShowExactDueDate(false)}
      onFocus={() => setShowExactDueDate(true)}
      onMouseEnter={() => setShowExactDueDate(true)}
      onMouseLeave={() => setShowExactDueDate(false)}
    >
      <div className='h-x5 w-x5 background-brand-subtle-hover p-x1 flex items-center justify-center rounded-full'>
        <Icon color='brand' icon={['fal', 'file-alt']} />
      </div>
      <div className='flex flex-1 flex-col'>
        <Text>{assignment.name}</Text>
        <Text color={dueCopyColor} size='s'>
          {showExactDueDate ? getFullDueDate() : dueCopyText}
        </Text>
      </div>
      <div className='gap-x1 flex items-center'>
        {hasTeacherFeedback && (
          <>
            {/* `display='flex'` because the wrapper is inline-block by default, which would break
                this row's alignment. Mouse-only by design: the icon is not focusable, and giving it
                a tab stop inside a row that is one big Link would cost keyboard users more than the
                tooltip gains them — the sr-only label below is their channel. */}
            <WithTooltip content='View feedback' display='flex'>
              <Icon icon='message-lines' iconStyle='regular' color='brand' />
            </WithTooltip>
            {/* The glyph is the whole signal for sighted users; without this the row reads
                identically with and without feedback to a screen reader. */}
            <span className='sr-only'>Your teacher left feedback</span>
          </>
        )}
        <StatusBadge
          grade={assignment.grade}
          gradesReleased={assignment.gradesReleased}
          status={assignment.status}
        />
      </div>
    </Link>
  );
}
